import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Eye, EyeOff, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import type { AxiosError } from "axios";

import { Button } from "@/marketing/components/ui/button";
import { Input } from "@/marketing/components/ui/input";
import { cn } from "@/marketing/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/marketing/components/ui/dialog";
import type {
  CommunityCategory,
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from "@/marketing/models/community";
import { communityAdminAPIClient } from "@/marketing/infrastructure/influencer/community/communityAdminAPIClient";
import { COMMUNITY_KEYS } from "@/marketing/infrastructure/influencer/community/utils/keys";
import { COMMUNITY_QUERIES } from "@/marketing/infrastructure/influencer/community/utils/queries";

interface CategoryManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const errorMessage = (error: AxiosError<{ message?: string }>, fallback: string) =>
  error.response?.data?.message ?? fallback;

// Every action (create / edit / reorder) persists immediately and refetches.
export function CategoryManagerDialog({ open, onOpenChange }: CategoryManagerDialogProps) {
  const queryClient = useQueryClient();
  const categoriesQuery = useQuery({ ...COMMUNITY_QUERIES.categories(), enabled: open });
  const categories = categoriesQuery.data ?? [];
  const activeCategories = categories.filter((category) => category.isActive);
  const activeIndexById = new Map(activeCategories.map((category, index) => [category.categoryId, index]));

  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newIsActive, setNewIsActive] = useState(true);

  const invalidateCommunity = () => queryClient.invalidateQueries({ queryKey: COMMUNITY_KEYS.all });

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setEditingId(null);
      setIsCreating(false);
      setNewName("");
      setNewIsActive(true);
    }
    onOpenChange(nextOpen);
  };

  const reorder = useMutation({
    mutationFn: (orderedCategoryIds: string[]) => communityAdminAPIClient.reorderCategories(orderedCategoryIds),
    onSuccess: () => invalidateCommunity(),
    onError: (error: AxiosError<{ message?: string }>) => toast.error(errorMessage(error, "Failed to reorder categories")),
  });

  const saveCategory = useMutation({
    mutationFn: ({ categoryId, request }: { categoryId: string; request: UpdateCategoryRequest }) =>
      communityAdminAPIClient.updateCategory(categoryId, request),
    onSuccess: () => {
      setEditingId(null);
      invalidateCommunity();
      toast.success("Category saved");
    },
    onError: (error: AxiosError<{ message?: string }>) => toast.error(errorMessage(error, "Failed to save category")),
  });

  const createCategory = useMutation({
    mutationFn: (request: CreateCategoryRequest) => communityAdminAPIClient.createCategory(request),
    onSuccess: () => {
      setIsCreating(false);
      setNewName("");
      setNewIsActive(true);
      invalidateCommunity();
      toast.success("Category created");
    },
    onError: (error: AxiosError<{ message?: string }>) => toast.error(errorMessage(error, "Failed to create category")),
  });

  const moveCategory = (activeIndex: number, direction: -1 | 1) => {
    if (activeIndex < 0) return;
    const target = activeIndex + direction;
    if (target < 0 || target >= activeCategories.length) return;

    const reordered = [...activeCategories];
    const current = reordered[activeIndex];
    const swapped = reordered[target];
    if (current === undefined || swapped === undefined) return;
    reordered[activeIndex] = swapped;
    reordered[target] = current;
    reorder.mutate(reordered.map((category) => category.categoryId));
  };

  const trimmedNewName = newName.trim();

  const handleOpenCreateForm = () => {
    setIsCreating(true);
    setNewName("");
    setNewIsActive(true);
  };

  const handleCancelCreateForm = () => {
    setIsCreating(false);
    setNewName("");
    setNewIsActive(true);
  };

  const handleSubmitNewCategory = () => {
    if (!trimmedNewName || createCategory.isPending) return;
    createCategory.mutate({ name: trimmedNewName, isActive: newIsActive });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="tw-max-w-lg">
        <DialogHeader>
          <DialogTitle>Manage Categories</DialogTitle>
        </DialogHeader>

        <div className="tw-flex tw-max-h-[55vh] tw-flex-col tw-gap-2 tw-overflow-y-auto tw-px-1">
          {categoriesQuery.isLoading ? (
            <div className="tw-py-8 tw-text-center tw-text-sm tw-text-gray-400">Loading categories...</div>
          ) : categoriesQuery.isError ? (
            <div className="tw-py-8 tw-text-center tw-text-sm tw-text-red-500">Failed to load categories.</div>
          ) : categories.length === 0 ? (
            <div className="tw-rounded-xl tw-border tw-border-dashed tw-border-gray-200 tw-py-8 tw-text-center tw-text-sm tw-text-gray-400">
              No categories yet — add the first one below.
            </div>
          ) : (
            categories.map((category) => {
              const activeIndex = activeIndexById.get(category.categoryId) ?? -1;
              return (
                <CategoryManagerRow
                  key={category.categoryId}
                  category={category}
                  isEditing={editingId === category.categoryId}
                  isSaving={saveCategory.isPending}
                  isReordering={reorder.isPending}
                  canMoveUp={activeIndex > 0}
                  canMoveDown={activeIndex >= 0 && activeIndex < activeCategories.length - 1}
                  onMoveUp={() => moveCategory(activeIndex, -1)}
                  onMoveDown={() => moveCategory(activeIndex, 1)}
                  onEdit={() => setEditingId(category.categoryId)}
                  onCancelEdit={() => setEditingId(null)}
                  onSave={(request) => saveCategory.mutate({ categoryId: category.categoryId, request })}
                />
              );
            })
          )}
        </div>

        <div className="tw-border-t tw-border-gray-200 tw-pt-3">
          {isCreating ? (
            <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-px-3 tw-py-3">
              <Input
                autoFocus
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleSubmitNewCategory();
                }}
                placeholder="Category name"
                className="tw-flex-1"
              />
              <ActiveToggle isActive={newIsActive} onToggle={() => setNewIsActive((current) => !current)} />
              <Button size="sm" onClick={handleSubmitNewCategory} disabled={!trimmedNewName || createCategory.isPending}>
                {createCategory.isPending ? "Creating..." : "Create"}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCancelCreateForm}>
                Cancel
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleOpenCreateForm}
              className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-1 tw-rounded-xl tw-border tw-border-dashed tw-border-gray-200 tw-py-3 tw-text-sm tw-font-medium tw-text-gray-500 hover:tw-border-violet-400 hover:tw-text-violet-700"
            >
              <Plus className="tw-h-4 tw-w-4" /> New Category
            </button>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CategoryManagerRowProps {
  category: CommunityCategory;
  isEditing: boolean;
  isSaving: boolean;
  isReordering: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: (request: UpdateCategoryRequest) => void;
}

function CategoryManagerRow({
  category,
  isEditing,
  isSaving,
  isReordering,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onEdit,
  onCancelEdit,
  onSave,
}: CategoryManagerRowProps) {
  return (
    <div className={cn("tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white", !category.isActive && "tw-border-gray-100 tw-bg-gray-50 tw-opacity-70")}>
      <div className="tw-flex tw-items-center tw-gap-2 tw-p-3">
        {category.isActive ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-text-gray-300">
            <button
              type="button"
              aria-label="Move up"
              onClick={onMoveUp}
              disabled={!canMoveUp || isReordering}
              className="hover:tw-text-gray-600 disabled:tw-opacity-30"
            >
              <ChevronUp className="tw-h-4 tw-w-4" />
            </button>
            <button
              type="button"
              aria-label="Move down"
              onClick={onMoveDown}
              disabled={!canMoveDown || isReordering}
              className="hover:tw-text-gray-600 disabled:tw-opacity-30"
            >
              <ChevronDown className="tw-h-4 tw-w-4" />
            </button>
          </div>
        ) : (
          // Inactive rows sort by recency, so they have no manual order.
          <div className="tw-w-4 tw-shrink-0" aria-hidden />
        )}
        <span className={cn("tw-flex-1 tw-truncate tw-text-sm tw-font-medium", category.isActive ? "tw-text-gray-900" : "tw-text-gray-500")}>{category.name}</span>
        {!category.isActive && (
          <span className="tw-rounded-full tw-bg-gray-100 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-gray-500">Inactive</span>
        )}
        <Button variant="outline" size="sm" onClick={isEditing ? onCancelEdit : onEdit}>
          <Pencil className="tw-mr-1 tw-h-3.5 tw-w-3.5" /> {isEditing ? "Close" : "Edit"}
        </Button>
      </div>

      {isEditing && <CategoryEditForm category={category} isSaving={isSaving} onSave={onSave} />}
    </div>
  );
}

interface CategoryEditFormProps {
  category: CommunityCategory;
  isSaving: boolean;
  onSave: (request: UpdateCategoryRequest) => void;
}

// Draft state seeds from the category at mount only, so refetch cannot overwrite an in-progress edit.
function CategoryEditForm({ category, isSaving, onSave }: CategoryEditFormProps) {
  const [name, setName] = useState(category.name);
  const [isActive, setIsActive] = useState(category.isActive);
  const trimmedName = name.trim();

  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-border-t tw-border-gray-100 tw-px-3 tw-py-3">
      <Input value={name} onChange={(event) => setName(event.target.value)} className="tw-flex-1" />
      <ActiveToggle isActive={isActive} onToggle={() => setIsActive((current) => !current)} />
      <Button size="sm" onClick={() => onSave({ name: trimmedName, isActive })} disabled={isSaving || trimmedName === ""}>
        {isSaving ? "Saving..." : "Save"}
      </Button>
    </div>
  );
}

interface ActiveToggleProps {
  isActive: boolean;
  onToggle: () => void;
}

function ActiveToggle({ isActive, onToggle }: ActiveToggleProps) {
  return (
    <button
      type="button"
      aria-label={isActive ? "Active — click to deactivate" : "Inactive — click to activate"}
      onClick={onToggle}
      className={cn(
        "tw-rounded-lg tw-border tw-p-2 tw-transition-colors",
        isActive ? "tw-border-emerald-200 tw-text-emerald-600" : "tw-border-gray-200 tw-text-gray-400",
      )}
    >
      {isActive ? <Eye className="tw-h-4 tw-w-4" /> : <EyeOff className="tw-h-4 tw-w-4" />}
    </button>
  );
}
